import { createContext, useContext } from "react";
import { createStore, StoreApi, useStore } from "zustand";
import { immer } from "zustand/middleware/immer";
import { useShallow } from "zustand/react/shallow";

import { RollForwardWizardStep } from "../roll_forward_wizard.constants";
import {
  RollForwardWizardActions,
  RollForwardWizardInitialState,
  RollForwardWizardStore
} from "./roll_forward_wizard_store_types";
import {
  createContinuityActions,
  createContinuitySlice,
  createFiltersActions,
  createFiltersSlice,
  createSourceTargetActions,
  createSourceTargetSlice
} from "./slices";

export const createRollForwardWizardStore = (initialState?: RollForwardWizardInitialState) =>
  createStore<RollForwardWizardStore>()(
    immer((set, get, api) => {
      const actions: RollForwardWizardActions = {
        navigation: {
          setActiveStep: (step) =>
            set((state) => {
              state.activeStep = step;
            })
        },
        filters: createFiltersActions(),
        sourceTarget: createSourceTargetActions(),
        continuity: createContinuityActions()
      };

      return {
        activeStep: RollForwardWizardStep.Filters,
        ...createFiltersSlice(set, get, api),
        ...createSourceTargetSlice(set, get, api),
        ...createContinuitySlice(set, get, api),
        ...initialState,
        actions
      };
    })
  );

export const RollForwardWizardStoreContext = createContext<StoreApi<RollForwardWizardStore> | null>(null);

const useRollForwardWizardStoreContext = (): StoreApi<RollForwardWizardStore> => {
  const store = useContext(RollForwardWizardStoreContext);

  if (!store) {
    throw new Error("useRollForwardWizardStoreContext must be used within RollForwardWizardStoreProvider");
  }

  return store;
};

export const useRollForwardWizardStore = <T>(selector: (state: RollForwardWizardStore) => T): T =>
  useStore(useRollForwardWizardStoreContext(), useShallow(selector));

export const useRollForwardWizardStoreActions = (): RollForwardWizardActions =>
  useStore(useRollForwardWizardStoreContext(), (state) => state.actions);
